// ============================================================================
// TENANT APPLICATION ASSIGNMENT SERVICE
// ============================================================================
// Business logic and API calls for the Tenant Application Assignments module.
//
// Routes calls across two backends:
//   - Cross-tenant queries  → /tenant-app-assignments     (TenantAppAssignmentsController)
//   - Per-record CRUD       → /tenants/{id}/applications  (TenantApplicationsController)
//
// Suspend/Resume implemented via PUT (no PATCH endpoint) by reading the
// current assignment, flipping is_suspended, and writing it back.

import { get, post, put, del } from '../client/axiosClient';
import { tenantAppAssignmentEndpoints } from '../endpoints/tenantAppAssignments.endpoints';
import { applicationEndpoints } from '../endpoints/applications.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ID,
  TenantAppAssignment,
  TenantAppAssignmentSearchRequest,
  TenantAppAssignmentStatistics,
  CreateTenantApplicationRequest,
  UpdateTenantApplicationRequest,
  TenantHubPagedResult,
  ApplicationResponse,
} from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';

class TenantAppAssignmentService {
  // ──────────────────────────────────────────────────────────────────
  // Cross-tenant queries
  // ──────────────────────────────────────────────────────────────────

  /**
   * Paginated list of assignments across all tenants.
   * GET /tenant-app-assignments
   */
  async getAll(
    params?: TenantAppAssignmentSearchRequest
  ): Promise<TenantHubApiResponse<TenantHubPagedResult<TenantAppAssignment>>> {
    const endpoint = params
      ? buildEndpointWithQuery(tenantAppAssignmentEndpoints.list(), params)
      : tenantAppAssignmentEndpoints.list();

    const response = await get<TenantHubApiResponse<TenantHubPagedResult<TenantAppAssignment>>>(endpoint);
    return response.data;
  }

  /**
   * Aggregate counts across all tenants.
   * GET /tenant-app-assignments/statistics
   */
  async getStatistics(): Promise<TenantHubApiResponse<TenantAppAssignmentStatistics>> {
    const response = await get<TenantHubApiResponse<TenantAppAssignmentStatistics>>(
      tenantAppAssignmentEndpoints.statistics()
    );
    return response.data;
  }

  // ──────────────────────────────────────────────────────────────────
  // Per-record CRUD (delegates to existing per-tenant controller)
  // ──────────────────────────────────────────────────────────────────

  /**
   * Get a single assignment.
   * GET /tenants/:tenantId/applications/:appId
   */
  async getById(tenantId: ID, appId: ID): Promise<TenantHubApiResponse<TenantAppAssignment>> {
    const response = await get<TenantHubApiResponse<TenantAppAssignment>>(
      tenantAppAssignmentEndpoints.getById(tenantId, appId)
    );
    return response.data;
  }

  /**
   * Create a new assignment for a tenant.
   * POST /tenants/:tenantId/applications
   */
  async create(
    tenantId: ID,
    payload: CreateTenantApplicationRequest
  ): Promise<TenantHubApiResponse<TenantAppAssignment>> {
    const response = await post<TenantHubApiResponse<TenantAppAssignment>>(
      tenantAppAssignmentEndpoints.create(tenantId),
      payload
    );
    return response.data;
  }

  /**
   * Update an existing assignment.
   * PUT /tenants/:tenantId/applications/:appId
   */
  async update(
    tenantId: ID,
    appId: ID,
    payload: UpdateTenantApplicationRequest
  ): Promise<TenantHubApiResponse<TenantAppAssignment>> {
    const response = await put<TenantHubApiResponse<TenantAppAssignment>>(
      tenantAppAssignmentEndpoints.update(tenantId, appId),
      payload
    );
    return response.data;
  }

  /**
   * Soft-remove an assignment.
   * DELETE /tenants/:tenantId/applications/:appId
   */
  async remove(tenantId: ID, appId: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(
      tenantAppAssignmentEndpoints.remove(tenantId, appId)
    );
    return response.data;
  }

  // ──────────────────────────────────────────────────────────────────
  // Suspend / Resume — PUT-based wrappers (no PATCH endpoint)
  // ──────────────────────────────────────────────────────────────────

  /**
   * Suspend an assignment with a reason. Reads current state, flips is_suspended.
   */
  async suspend(
    tenantId: ID,
    appId: ID,
    reason: string
  ): Promise<TenantHubApiResponse<TenantAppAssignment>> {
    const current = await this.getById(tenantId, appId);
    if (!current.success || !current.data) {
      throw new Error(current.message || 'Failed to fetch assignment for suspend');
    }
    return this.update(tenantId, appId, {
      ...this.toUpdatePayload(current.data),
      isSuspended: true,
      suspensionReason: reason,
    });
  }

  /**
   * Resume a suspended assignment. Clears is_suspended + suspensionReason.
   */
  async resume(tenantId: ID, appId: ID): Promise<TenantHubApiResponse<TenantAppAssignment>> {
    const current = await this.getById(tenantId, appId);
    if (!current.success || !current.data) {
      throw new Error(current.message || 'Failed to fetch assignment for resume');
    }
    return this.update(tenantId, appId, {
      ...this.toUpdatePayload(current.data),
      isSuspended: false,
      suspensionReason: undefined,
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Available apps — frontend-computed (no new backend endpoint)
  // ──────────────────────────────────────────────────────────────────

  /**
   * Returns applications NOT yet assigned to the given tenant.
   * Used by the form's app picker so admins can't double-assign.
   */
  async getAvailableApps(tenantId: ID): Promise<ApplicationResponse[]> {
    const [allAppsRes, assignedRes] = await Promise.all([
      get<TenantHubApiResponse<ApplicationResponse[]>>(applicationEndpoints.list()),
      // includeSuspended: a suspended assignment still blocks re-assignment on the
      // backend, so it must count as "assigned" here. Removed assignments are
      // excluded on purpose — the backend allows re-assigning those.
      this.getAll({ tenantIds: [tenantId], includeSuspended: true, pageSize: 500, page: 1 }),
    ]);

    const allApps = allAppsRes.data?.data ?? [];
    const assignedItems = assignedRes.data?.items ?? [];
    const assignedIds = new Set<string>(assignedItems.map((a) => String(a.appId)));

    return allApps.filter((app) => !assignedIds.has(String(app.appId)));
  }

  // ──────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────

  /**
   * Builds a clean update payload from a fetched assignment, stripping
   * server-derived fields and joined identity columns. Used by suspend/resume.
   */
  private toUpdatePayload(a: TenantAppAssignment): UpdateTenantApplicationRequest {
    return {
      appName: a.appName,
      appCode: a.appCode,
      appVersion: a.tenantAppVersion ?? a.appVersion,
      keycloakClientId: a.keycloakClientId,
      callbackUrl: a.callbackUrl,
      logoutUrl: a.logoutUrl,
      dbProvider: a.dbProvider,
      dbName: a.dbName,
      dbHost: a.dbHost,
      dbPort: a.dbPort,
      dbUsername: a.dbUsername,
      subscriptionPlanId: a.subscriptionPlanId,
      subscriptionPlanName: a.subscriptionPlanName,
      billingCycle: a.billingCycle,
      subscriptionStartDate: a.subscriptionStartDate,
      subscriptionEndDate: a.subscriptionEndDate,
      billingEmail: a.billingEmail,
      isTrial: a.isTrial,
      trialEndDate: a.trialEndDate,
      maxUsers: a.maxUsers,
      maxStorageGb: a.maxStorageGb,
      apiRateLimit: a.apiRateLimit,
      isSuspended: a.isSuspended,
      suspensionReason: a.suspensionReason,
    };
  }
}

export const tenantAppAssignmentService = new TenantAppAssignmentService();
export default tenantAppAssignmentService;
